import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { setupProgramForUser, getCurrentProgram, getUserProfile, shiftAllActivities, clearAllScheduledActivities } from '../utils/database';
import samProgram from '../data/programs/sam-multi-sport-sprint-triathlon.json';

/**
 * Temporary admin page to set up Sam's Multi-Sport Sprint Triathlon program
 * Access at: /{userId}/setup-program
 */
function SetupProgram() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [currentProgram, setCurrentProgram] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check auth and load current program
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user || user.uid !== userId) {
        navigate('/signin');
        return;
      }
      setAuthChecked(true);
      
      // Check if user already has a program
      try {
        const program = await getCurrentProgram(userId);
        setCurrentProgram(program);
      } catch (err) {
        console.error('Error checking current program:', err);
      }
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  const handleShiftActivities = async (days) => {
    setLoading(true);
    setError('');
    setStatus(`Shifting all activities by ${days} day(s)...`);
    
    try {
      const count = await shiftAllActivities(userId, days);
      setStatus(`✅ Successfully shifted ${count} activities by ${days} day(s)!`);
    } catch (err) {
      console.error('Shift error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetAndRebuild = async () => {
    setLoading(true);
    setError('');
    setStatus('Clearing existing scheduled activities...');
    
    try {
      // Clear existing activities
      const deletedCount = await clearAllScheduledActivities(userId);
      setStatus(`Deleted ${deletedCount} activities. Now rebuilding calendar...`);
      
      // Re-run setup
      const result = await setupProgramForUser(userId, samProgram);
      
      if (result.success) {
        setStatus(`✅ Calendar rebuilt! Created new activities with seasonal templates.`);
        
        // Reload current program
        const program = await getCurrentProgram(userId);
        setCurrentProgram(program);
      } else {
        setError('Program rebuild returned unsuccessful');
      }
    } catch (err) {
      console.error('Reset error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupProgram = async () => {
    setLoading(true);
    setError('');
    setStatus('Starting program setup...');

    try {
      // Check account status - require liability waiver before program setup
      setStatus('Checking account status...');
      const userProfile = await getUserProfile(userId);
      
      if (!userProfile?.liabilityWaiverAccepted) {
        setError('Liability waiver must be accepted before setting up a program. Please complete onboarding.');
        setLoading(false);
        return;
      }
      
      if (!userProfile?.termsAccepted) {
        setError('Terms of service must be accepted before setting up a program.');
        setLoading(false);
        return;
      }
      
      setStatus('Creating program, macrocycles, and mesocycles...');
      const result = await setupProgramForUser(userId, samProgram);
      
      if (result.success) {
        setStatus(`✅ Program "${samProgram.name}" setup complete!`);
        
        // Reload current program
        const program = await getCurrentProgram(userId);
        setCurrentProgram(program);
      } else {
        setError('Program setup returned unsuccessful');
      }
    } catch (err) {
      console.error('Setup error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!authChecked) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Program Setup</h1>
        <p style={styles.subtitle}>Admin page for setting up training programs</p>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Available Program</h2>
          <div style={styles.programCard}>
            <h3 style={styles.programName}>{samProgram.name}</h3>
            <p style={styles.programDesc}>{samProgram.description}</p>
            <div style={styles.programMeta}>
              <span>Duration: {samProgram.durationWeeks} weeks</span>
              <span>Difficulty: {samProgram.difficulty}</span>
              <span>Strength: {samProgram.strengthSessionsPerWeek}x/week</span>
            </div>
            <div style={styles.programMeta}>
              <span>Macrocycles: {samProgram.macrocycles.length}</span>
              <span>Key Events: {samProgram.keyEvents.length}</span>
            </div>
          </div>
        </div>

        {currentProgram?.program && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Current Program</h2>
            <div style={styles.currentProgram}>
              <p><strong>Program:</strong> {currentProgram.program.name}</p>
              <p><strong>Macrocycle:</strong> {currentProgram.macrocycle?.name || 'None'}</p>
              <p><strong>Mesocycle:</strong> {currentProgram.mesocycle?.name || 'None'}</p>
              <p><strong>Current Week:</strong> {currentProgram.currentWeek || 1}</p>
            </div>
          </div>
        )}

        <div style={styles.actions}>
          <button 
            onClick={handleSetupProgram} 
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Setting up...' : 'Setup Multi-Sport Sprint Triathlon Program'}
          </button>

          <button 
            onClick={() => navigate(`/${userId}/dashboard`)}
            style={styles.secondaryButton}
          >
            Back to Dashboard
          </button>
        </div>

        {currentProgram?.program && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Schedule Adjustments</h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => handleShiftActivities(-1)} 
                disabled={loading}
                style={{
                  ...styles.secondaryButton,
                  border: '1px solid #FE6022',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Shift All Activities -1 Day (Earlier)
              </button>
              <button 
                onClick={() => handleShiftActivities(1)} 
                disabled={loading}
                style={{
                  ...styles.secondaryButton,
                  opacity: loading ? 0.6 : 1
                }}
              >
                Shift All Activities +1 Day (Later)
              </button>
              <button 
                onClick={handleResetAndRebuild} 
                disabled={loading}
                style={{
                  ...styles.button,
                  background: '#ef4444',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Reset & Rebuild Calendar (with new seasonal templates)
              </button>
            </div>
          </div>
        )}

        {status && (
          <div style={styles.status}>
            {status}
          </div>
        )}

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Key Events</h2>
          <ul style={styles.eventList}>
            {samProgram.keyEvents.map((event, i) => (
              <li key={i} style={styles.eventItem}>
                <span style={styles.eventDate}>{event.date}</span>
                <span style={styles.eventName}>{event.name}</span>
                {event.details && <span style={styles.eventDetails}>{event.details}</span>}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Macrocycles</h2>
          {samProgram.macrocycles.map((macro, i) => (
            <div key={i} style={styles.macroCard}>
              <h3 style={styles.macroName}>{macro.name}</h3>
              <p style={styles.macroDates}>{macro.startDate} → {macro.endDate}</p>
              <p style={styles.macroPhase}>Phase: {macro.phase}</p>
              <div style={styles.mesoList}>
                {macro.mesocycles.map((meso, j) => (
                  <div key={j} style={styles.mesoItem}>
                    <span style={styles.mesoName}>{meso.name}</span>
                    <span style={styles.mesoDuration}>{meso.totalWeeks} weeks</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0D0D0D',
    padding: '40px 20px',
    color: 'white',
    fontFamily: 'Poppins, sans-serif'
  },
  card: {
    maxWidth: '800px',
    margin: '0 auto',
    background: 'linear-gradient(180deg, rgba(55, 55, 55, 0.43) 0%, rgba(60, 60, 60, 0.43) 100%)',
    borderRadius: '40px',
    padding: '40px',
    border: '1px solid rgba(58, 58, 58, 0.30)'
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#FE6022',
    WebkitTextStroke: '0',
    WebkitTextStrokeWidth: '0',
    textTransform: 'none',
    letterSpacing: 'normal',
    textAlign: 'left',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '16px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '32px'
  },
  section: {
    marginBottom: '32px'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '500',
    marginBottom: '16px',
    color: 'white'
  },
  programCard: {
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '20px',
    padding: '20px',
    border: '1px solid rgba(254, 96, 34, 0.3)'
  },
  programName: {
    fontSize: '18px',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#FE6022'
  },
  programDesc: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.8)',
    marginBottom: '12px'
  },
  programMeta: {
    display: 'flex',
    gap: '20px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    marginTop: '8px'
  },
  currentProgram: {
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '12px',
    padding: '16px',
    fontSize: '14px'
  },
  actions: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  button: {
    background: '#FE6022',
    color: 'black',
    border: 'none',
    borderRadius: '30px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  secondaryButton: {
    background: 'transparent',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '30px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '400',
    cursor: 'pointer'
  },
  status: {
    background: 'rgba(34, 197, 94, 0.2)',
    border: '1px solid rgba(34, 197, 94, 0.5)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    color: '#22c55e'
  },
  error: {
    background: 'rgba(239, 68, 68, 0.2)',
    border: '1px solid rgba(239, 68, 68, 0.5)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    color: '#ef4444'
  },
  eventList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  eventItem: {
    display: 'flex',
    gap: '16px',
    padding: '12px 0',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  eventDate: {
    color: '#FE6022',
    fontWeight: '500',
    minWidth: '100px'
  },
  eventName: {
    color: 'white',
    flex: 1
  },
  eventDetails: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px'
  },
  macroCard: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '16px'
  },
  macroName: {
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '4px'
  },
  macroDates: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '4px'
  },
  macroPhase: {
    fontSize: '13px',
    color: '#FE6022',
    marginBottom: '12px'
  },
  mesoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  mesoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    background: 'rgba(255,255,255,0.05)',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '13px'
  },
  mesoName: {
    color: 'white'
  },
  mesoDuration: {
    color: 'rgba(255,255,255,0.6)'
  }
};

export default SetupProgram;

